import {
   DirectoSmsProviderSettings,
   QuicklySmsProviderSettings,
   TwilioSmsProviderSettings,
} from "@/types/settings";

type Translate = (key: string) => string;

const PHONE_PATTERN = /^\+?\d{7,15}$/;

function isValidHttpUrl(value: string): boolean {
   try {
      const parsed = new URL(value);
      return parsed.protocol === "http:" || parsed.protocol === "https:";
   } catch {
      return false;
   }
}

function phoneError(value: string, t: Translate): string | undefined {
   if (!value.trim()) return t("smsIntegration.form.phoneNumberRequired");
   if (!PHONE_PATTERN.test(value.trim())) return t("smsIntegration.form.phoneNumberInvalid");
   return undefined;
}

export interface TwilioSmsFormValues {
   phoneNumber: string;
   accountSid: string;
   messagingServiceSid: string;
   authToken: string;
}

export interface QuicklySmsFormValues {
   url: string;
   apiKey: string;
   apiSecret: string;
}

export interface DirectoSmsFormValues {
   fromPhoneNumber: string;
}

export type TwilioSmsFormErrors = Partial<Record<keyof TwilioSmsFormValues, string>>;
export type QuicklySmsFormErrors = Partial<Record<keyof QuicklySmsFormValues, string>>;
export type DirectoSmsFormErrors = Partial<Record<keyof DirectoSmsFormValues, string>>;

export const EMPTY_TWILIO_VALUES: TwilioSmsFormValues = {
   phoneNumber: "",
   accountSid: "",
   messagingServiceSid: "",
   authToken: "",
};

export const EMPTY_QUICKLY_VALUES: QuicklySmsFormValues = {
   url: "",
   apiKey: "",
   apiSecret: "",
};

export const EMPTY_DIRECTO_VALUES: DirectoSmsFormValues = {fromPhoneNumber: ""};

export function twilioValuesFromProvider(provider: TwilioSmsProviderSettings): TwilioSmsFormValues {
   return {
      phoneNumber: provider.phoneNumber ?? "",
      accountSid: provider.accountSid ?? "",
      messagingServiceSid: provider.messagingServiceSid ?? "",
      authToken: "",
   };
}

export function quicklyValuesFromProvider(
   provider: QuicklySmsProviderSettings
): QuicklySmsFormValues {
   return {
      url: provider.url ?? "",
      apiKey: "",
      apiSecret: "",
   };
}

export function directoValuesFromProvider(
   provider: DirectoSmsProviderSettings
): DirectoSmsFormValues {
   return {fromPhoneNumber: provider.fromPhoneNumber ?? ""};
}

export function validateTwilio(
   values: TwilioSmsFormValues,
   requireAuthToken: boolean,
   t: Translate
): TwilioSmsFormErrors {
   const errors: TwilioSmsFormErrors = {};
   const phone = phoneError(values.phoneNumber, t);
   if (phone) {
      errors.phoneNumber = phone;
   }
   if (!values.accountSid.trim()) {
      errors.accountSid = t("smsIntegration.form.accountSidRequired");
   }
   // The Twilio send path throws without a messaging service SID even though
   // the API model marks it optional.
   if (!values.messagingServiceSid.trim()) {
      errors.messagingServiceSid = t("smsIntegration.form.messagingServiceSidRequired");
   }
   if (requireAuthToken && !values.authToken.trim()) {
      errors.authToken = t("smsIntegration.form.authTokenRequired");
   }
   return errors;
}

export function validateQuickly(
   values: QuicklySmsFormValues,
   requireSecrets: boolean,
   t: Translate
): QuicklySmsFormErrors {
   const errors: QuicklySmsFormErrors = {};
   if (!values.url.trim()) {
      errors.url = t("smsIntegration.form.urlRequired");
   } else if (!isValidHttpUrl(values.url.trim())) {
      errors.url = t("smsIntegration.form.urlInvalid");
   }
   if (requireSecrets && !values.apiKey.trim()) {
      errors.apiKey = t("smsIntegration.form.apiKeyRequired");
   }
   if (requireSecrets && !values.apiSecret.trim()) {
      errors.apiSecret = t("smsIntegration.form.apiSecretRequired");
   }
   return errors;
}

export function validateDirecto(
   values: DirectoSmsFormValues,
   t: Translate
): DirectoSmsFormErrors {
   const phone = phoneError(values.fromPhoneNumber, t);
   return phone ? {fromPhoneNumber: phone} : {};
}
